import { VideoGlContext } from '../video-context';
import { VideoNode } from './base';

export class YUVSourceNode<S extends TexImageSource = TexImageSource> extends VideoNode<S, VideoGlContext> {
  private yTextureRef: WebGLTexture | null;
  private uTextureRef: WebGLTexture | null;
  private vTextureRef: WebGLTexture | null;
  public Y: Uint8Array;
  public U: Uint8Array;
  public V: Uint8Array;
  constructor(context: VideoGlContext) {
    super(context, {
      name: 'yuv-source',
      useDefaultProgram: false,
      create2d: false,
      useFbo: false,
      createTexture: false,
      logger: context.log,
      fragmentShaderSource: `
        precision highp float;
        uniform sampler2D ySampler;
        uniform sampler2D uSampler;
        uniform sampler2D vSampler;
        varying highp vec2 textureCoord;
        const mat4 YUV2RGB = mat4(
        1.1643828125, 0, 1.59602734375, -.87078515625,
        1.1643828125, -.39176171875, -.81296875, .52959375,
        1.1643828125, 2.017234375, 0, -1.081390625,
        0, 0, 0, 1);
        void main() {
          vec3 yuv;
          yuv.r = texture2D(ySampler, textureCoord).r;
          yuv.g = texture2D(uSampler, textureCoord).r;
          yuv.b = texture2D(vSampler, textureCoord).r;
          gl_FragColor = vec4(yuv,1) * YUV2RGB;
        }
          `,
      vertexShaderSource: `
          attribute vec4 vertexPos;
          attribute vec2 texturePos;
          varying vec2 textureCoord;
          void main() {
            gl_Position = vertexPos;
            textureCoord = texturePos;
            }`
    });
    this.useProgram();
    const gl = this.context.ctx!;
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    this.setTexBuffer([
      0, 1, // 左上
      1, 1, // 右上
      0, 0, // 左下
      1, 0 // 右下
    ]);
    this.yTextureRef = this._initTexture('ySampler', 0);
    this.uTextureRef = this._initTexture('uSampler', 1);
    this.vTextureRef = this._initTexture('vSampler', 2);
    this._canvas = context._canvas;
  }
  _initTexture(name:string, index:number) {
    const gl = this.context.ctx!;
    const textureRef = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, textureRef);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.uniform1i(gl.getUniformLocation(this.program!, name), index);
    return textureRef;
  }
  render(seq: number): boolean {
    const gl = this.context.ctx!;
    const w = this.width;
    const h = this.height;
    this.useProgram();
    // this.useBufferFrame();
    gl.viewport(0, 0, w, h);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.yTextureRef);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w, h, gl.LUMINANCE, gl.UNSIGNED_BYTE, this.Y);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.uTextureRef);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w / 2, h / 2, gl.LUMINANCE, gl.UNSIGNED_BYTE, this.U);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.vTextureRef);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w / 2, h / 2, gl.LUMINANCE, gl.UNSIGNED_BYTE, this.V);
    this.draw();
    return true;
  }
  resize(width: number, height: number) {
    super.resize(width, height);
    const gl = this.context.ctx!;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.yTextureRef);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, width, height, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, null);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.uTextureRef);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, width / 2, height / 2, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, null);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.vTextureRef);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, width / 2, height / 2, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, null);
  }
}
